/** @jsxImportSource theme-ui */
import React from 'react';
import { useSelector } from 'react-redux';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { mortalityRateToRangeString } from 'models/DiseaseMortalityRatesModels';
import { formatNumber, pluralize } from 'utils/stringFormattingHelpers';

import { BdSeverityIcon } from 'components/_common/Icon/BdSeverityIcon';

import diseasesSelectors from 'diseases/diseases.selectors';
import riskOverviewSelectors from 'riskOverview/riskOverview.selectors';

import RiskOverviewDetailColumnListItem from './RiskOverviewDetailColumnListItem';
import RiskOverviewDetailColumnNoDataListItem from './RiskOverviewDetailColumnNoDataListItem';
import {
  detailColumnListStyle,
  disruptionFactorSectionStyles,
  iconColumnStyle,
} from './RiskOverviewStyles';

type RiskOverviewCardSeverityDetailsProps = {
  diseaseActivityId: number;
  locationId: number;
};

const RiskOverviewCardSeverityDetails: React.FC<RiskOverviewCardSeverityDetailsProps> = ({
  diseaseActivityId,
  locationId,
}) => {
  const numOfRiskFactors = useSelector<InsightsState, number>(
    riskOverviewSelectors.getNumOfRiskFactors(diseaseActivityId, locationId)
  );
  const isTreatmentAvailable = useSelector<InsightsState, boolean>(
    riskOverviewSelectors.getIsTreatmentAvailable(diseaseActivityId, locationId)
  );
  const severityType = useSelector<InsightsState, dto.SeverityDisruptionFactorType>(
    riskOverviewSelectors.getSeverityByDiseaseActivityIdAndLocationId(diseaseActivityId, locationId)
  );
  const mortalityRates = useSelector(
    riskOverviewSelectors.getRiskFeedEntryMortalityRates(diseaseActivityId, locationId)
  );
  const diseaseId = useSelector<InsightsState, dto.SeverityDisruptionFactorType>(
    riskOverviewSelectors.getDiseaseIdByActivityIdAndLocationId(diseaseActivityId, locationId)
  );
  const diseaseName = useSelector<InsightsState, string>(
    diseasesSelectors.getDiseaseName(diseaseId)
  );

  const getDetails = () => {
    const detailPoints: string[] = [];
    switch (severityType) {
      case dto.SeverityDisruptionFactorType.Mild:
        detailPoints.push(
          `${diseaseName} often has a mild presentation, from which people may naturally recover`
        );
        break;
      case dto.SeverityDisruptionFactorType.MildToModerate:
        detailPoints.push(
          `${diseaseName} is often mild, but in rare cases can lead to long term complications or hospitalization`
        );
        break;
      case dto.SeverityDisruptionFactorType.Moderate:
        detailPoints.push(
          `${diseaseName} is a moderately severe disease, which can sometimes lead to long term complications or hospitalization`
        );
        break;
      case dto.SeverityDisruptionFactorType.ModerateToSevere:
        detailPoints.push(
          `${diseaseName} can sometimes lead to long term complications, hospitalization or death`
        );
        break;
      case dto.SeverityDisruptionFactorType.Severe:
        detailPoints.push(
          `${diseaseName} is a high severity disease, which can frequently lead to long term complications, hospitalization, and/or death`
        );
        break;
      default:
        break;
    }

    if (mortalityRates) {
      const treatedMortalityRate = mortalityRateToRangeString(
        mortalityRates.treatedMax,
        mortalityRates.treatedMin
      );
      const untreatedMortalityRate = mortalityRateToRangeString(
        mortalityRates.untreatedMax,
        mortalityRates.untreatedMin
      );

      if (treatedMortalityRate?.length && untreatedMortalityRate?.length) {
        detailPoints.push(
          `With early detection and treatment, the mortality rate is ${treatedMortalityRate}. Without treatment, the mortality rate is ${untreatedMortalityRate}`
        );
      } else if (treatedMortalityRate?.length) {
        detailPoints.push(
          `With early detection and treatment, the mortality rate is ${treatedMortalityRate}`
        );
      } else if (untreatedMortalityRate?.length) {
        detailPoints.push(`Without treatment, the mortality rate is ${untreatedMortalityRate}`);
      }
    }

    if (isTreatmentAvailable) {
      detailPoints.push(`There is a specific and effective treatment for ${diseaseName}`);
    } else if (isTreatmentAvailable === false) {
      // Hard check for false because nulls aren't to show a point
      detailPoints.push(`Treatment of ${diseaseName} is through supportive care only`);
    }

    if (numOfRiskFactors > 0) {
      detailPoints.push(
        `There ${pluralize(numOfRiskFactors, 'is', 'are')} ${formatNumber(
          numOfRiskFactors,
          'risk factor',
          'risk factors'
        )} that can contribute to more severe manifestation of disease and outcomes of infection`
      );
    }

    return detailPoints;
  };

  return (
    <div
      data-testid="Risk-Overview-Severity-Panel"
      className="ro-card-disrupt-factor-container"
      sx={{ ...disruptionFactorSectionStyles, borderBottom: 'none' }}
    >
      <div sx={iconColumnStyle} className="icon-column">
        <BdSeverityIcon type={severityType} />
      </div>
      <ul sx={detailColumnListStyle} className="detail-column">
        {getDetails().map((x, index) => (
          <RiskOverviewDetailColumnListItem key={index} itemText={x} />
        ))}
        {getDetails().length === 0 && <RiskOverviewDetailColumnNoDataListItem />}
      </ul>
    </div>
  );
};

export default RiskOverviewCardSeverityDetails;
